import {
  CodeIcon,
  ExportIcon,
  GlobeIcon,
  ShareNetworkIcon,
  SparkleIcon,
} from '@phosphor-icons/react'
import { useEffect, useMemo, useState } from 'react'
import { api, type Track } from './api.ts'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import { DownloadAudioModal } from './DownloadAudioModal.tsx'
import { exportAudioFromCode } from './export-audio.ts'
import { NavBar } from './NavBar.tsx'
import { ProfileImage } from './ProfileImage.tsx'
import { Link } from './Router.tsx'
import { ShareModal } from './ShareModal.tsx'
import { Spinner } from './Spinner.tsx'
import { useExportedAudioStore, useNavBarHeightStore } from './store.ts'
import { showToast, ToastContainer } from './Toast.tsx'
import { TrackList } from './TrackList.tsx'
import { useTrackList } from './useTrackList.ts'

interface TrackPageProps {
  trackId: string
}

export function TrackPage({ trackId }: TrackPageProps) {
  const [track, setTrack] = useState<Track | null>(null)
  const [loading, setLoading] = useState(true)
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false)
  const [showSharePopup, setShowSharePopup] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [exportBpm, setExportBpm] = useState(120)
  const { height: navBarHeight } = useNavBarHeightStore()
  const { exportedAudioUrl, exportedFilename, setExportedAudio, clearExportedAudio } = useExportedAudioStore()
  const params = new URLSearchParams(window.location.search)
  const hasNoAccount = params.has('noaccount')

  const getTrackUrl = (trackId: string) => {
    const baseUrl = `${window.location.origin}/loop/${trackId}`
    if (!hasNoAccount) return baseUrl
    return `${baseUrl}?noaccount`
  }

  const tracksArray = useMemo(() => (track ? [track] : []), [track])

  const trackList = useTrackList({
    pageType: 'trackPage',
    tracks: tracksArray,
    loading,
    onSingleTrackUpdate: setTrack,
  })

  useEffect(() => {
    loadTrack()
  }, [trackId])

  useEffect(() => {
    const checkScreenSize = () => {
      setIsVerySmallScreen(window.innerWidth < 700)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  useEffect(() => {
    if (track?.title && track?.userName) {
      document.title = `${track.title} by ${track.userName} - loopmaster`
    }
  }, [track])

  const loadTrack = async () => {
    try {
      setLoading(true)
      const response = await api.tracks.getTrack(trackId)
      setTrack(response.track)
    }
    catch (err: any) {
      showToast(err.message || 'Failed to load loop')
    }
    finally {
      setLoading(false)
    }
  }

  const handleShare = async () => {
    if (!track) return
    const trackUrl = getTrackUrl(track.id)
    const shareText = `Check out "${track.title}" by ${track.userName || 'Unknown'}`

    if (navigator.share) {
      try {
        await navigator.share({
          title: track.title,
          text: shareText,
          url: trackUrl,
        })
      }
      catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err)
        }
      }
    }
    else {
      setShowSharePopup(true)
    }
  }

  const handleExportClick = async () => {
    if (!track) return
    try {
      const compiler = new BytecodeCompiler()
      const program = compiler.compile(track.code)
      setExportBpm(program.bpm)
      setShowExportModal(true)
    }
    catch (err: any) {
      showToast(err.message || 'Please fix compilation errors before exporting')
    }
  }

  const handleDownloadAudio = async (bars: number, oversampling: 1 | 2 | 4 | 8) => {
    if (!track) return
    try {
      setIsExporting(true)
      clearExportedAudio()
      const { url, filename } = await exportAudioFromCode({
        code: track.code,
        globalBpm: exportBpm,
        bars,
        oversampling,
        title: track.title,
        artist: track.userName || undefined,
      })
      setExportedAudio(url, filename)
    }
    catch (err: any) {
      showToast(err.message || 'Failed to export audio')
    }
    finally {
      setIsExporting(false)
    }
  }

  const rightSideButtons = (
    <>
      <Link
        href="/"
        className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-lime-400 to-green-600 text-white hover:opacity-90 flex items-center gap-2"
      >
        <GlobeIcon weight="regular" size={16} />
        <span className="hidden md:inline">Browse All</span>
      </Link>
      {!isVerySmallScreen && (
        <>
          <Link
            href="/generate"
            className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 flex items-center gap-2"
          >
            <SparkleIcon weight="regular" size={16} />
            AI Generate
          </Link>
          <Link
            href="/editor"
            className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-500 to-red-600 text-white hover:opacity-90 flex items-center gap-2"
          >
            <CodeIcon weight="regular" size={16} />
            Switch to Editor
          </Link>
        </>
      )}
    </>
  )

  if (loading) {
    return (
      <div className="w-[100dvw] h-[100dvh] overflow-hidden text-[#f0f0f0] flex flex-col">
        <NavBar
          onPlay={trackList.handleNavBarPlay}
          onStop={trackList.handleNavBarStop}
          rightSideButtons={rightSideButtons}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-12 text-[#888] flex flex-col items-center gap-3">
            <div className="w-6 h-6">
              <Spinner />
            </div>
            <span>Loading track...</span>
          </div>
        </div>
      </div>
    )
  }

  if (!track && !loading) {
    return (
      <div className="w-[100dvw] h-[100dvh] overflow-hidden text-[#f0f0f0] flex flex-col">
        <NavBar
          onPlay={trackList.handleNavBarPlay}
          onStop={trackList.handleNavBarStop}
          rightSideButtons={rightSideButtons}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-red-500">Loop not found</div>
        </div>
        <ToastContainer />
      </div>
    )
  }

  return (
    <div className="w-[100dvw] h-[100dvh] overflow-hidden text-[#f0f0f0] flex flex-col">
      <NavBar
        onPlay={trackList.handleNavBarPlay}
        onStop={trackList.handleNavBarStop}
        rightSideButtons={rightSideButtons}
      />

      <div className="relative" style={{ height: `calc(100dvh - ${navBarHeight}px)` }}>
        <div className="relative h-full overflow-y-auto overflow-x-hidden p-10">
          {track && (
            <TrackList
              tracks={[track]}
              loading={false}
              header={
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-wrap items-center gap-3 text-3xl font-extrabold font-[Turret_Road] text-lime-400">
                    <Link
                      href={`/profile/${track.userId}`}
                      className="text-neutral-500 hover:text-orange-500 flex items-center gap-3"
                    >
                      <ProfileImage
                        userId={track.userId}
                        userName={track.userName || 'Unknown'}
                        size={40}
                        hasAvatar={track.userHasAvatar}
                      />

                      {track.userName || 'Unknown'}
                    </Link>{' '}
                    <span className="text-neutral-500">-</span>
                    <span>{track.title}</span>
                  </div>
                  <button
                    onClick={handleExportClick}
                    className="px-1 h-8 text-neutral-500 hover:text-orange-500 flex items-center gap-1"
                    title="Export Audio"
                  >
                    <ExportIcon weight="regular" size={26} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="px-1 h-8 text-neutral-500 hover:text-orange-500 flex items-center gap-1"
                    title="Share"
                  >
                    <ShareNetworkIcon weight="regular" size={26} />
                  </button>
                </div>
              }
              showTitle={false}
              activeTrackId={trackList.activeTrackId}
              actuallyPlaying={trackList.actuallyPlaying}
              trackParents={trackList.trackParents}
              trackRemixes={trackList.trackRemixes}
              likedTrackIds={trackList.likedTrackIds}
              activeRemixId={trackList.activeRemixId}
              compileMode={trackList.compileMode}
              expandCommentsByDefault={true}
              onSetActiveTrackId={trackList.setActiveTrackId}
              onSetActiveRemixId={trackList.setActiveRemixId}
              onSetActuallyPlaying={trackList.setActuallyPlaying}
              onLoad={trackList.handleLoad}
              onLoadInA={trackList.handleLoadInA}
              onLoadInB={trackList.handleLoadInB}
              onToggleLike={trackList.handleToggleLike}
              onPlayRemix={trackList.handlePlayRemix}
              onStopRemix={trackList.handleStopRemix}
            />
          )}
        </div>
      </div>

      {track && (
        <ShareModal
          isOpen={showSharePopup}
          onClose={() => setShowSharePopup(false)}
          trackUrl={getTrackUrl(track.id)}
          trackTitle={track.title}
          userName={track.userName}
        />
      )}
      {track && (
        <DownloadAudioModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleDownloadAudio}
          onDownload={() => {
            if (exportedAudioUrl) {
              const a = document.createElement('a')
              a.href = exportedAudioUrl
              a.download = exportedFilename
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }
          }}
          bpm={exportBpm}
          isExporting={isExporting}
          exportedAudioUrl={exportedAudioUrl}
          exportedFilename={exportedFilename}
        />
      )}
      <ToastContainer />
    </div>
  )
}
